import React, { useRef, useState } from 'react';

// On-screen Tamil character palette for admin data entry
// (take-order-i18n follow-up: translated menu names must be enterable by an admin
// who has NO Tamil keyboard configured on their machine).
//
// WHY A FLAT PALETTE IS CORRECT, AND WHY THERE IS NO COMBINING LOGIC HERE
// Tamil vowel signs (ா ி ீ ு …) and the pulli (்) are Unicode COMBINING marks.
// Appending one after a consonant produces the composed glyph automatically:
//   'க' + 'ா'  renders as  கா
//   'க' + '்'  renders as  க்
// So inserting characters one at a time into a plain string yields correct Tamil
// with no consonant x vowel matrix and no dependency. The text engine composes.
//
// WHAT THIS IS NOT
// This is a character palette, not phonetic transliteration. Typing "pattar" and
// getting பட்டர் would be faster for long menus, but a correct transliteration
// engine is a real dependency (and a bad hand-rolled one produces subtly wrong
// Tamil). This gets an admin unblocked with zero dependencies; phonetic input can
// layer on later without changing the stored data.
//
// Insertion happens at the caret, not blindly appended, so an admin can correct a
// mistake mid-word rather than retyping the whole name.

// 12 independent vowels — used when a word STARTS with a vowel sound.
const VOWELS = ['அ', 'ஆ', 'இ', 'ஈ', 'உ', 'ஊ', 'எ', 'ஏ', 'ஐ', 'ஒ', 'ஓ', 'ஔ'];

// 18 native consonants, in traditional order.
const CONSONANTS = [
  'க', 'ங', 'ச', 'ஞ', 'ட', 'ண',
  'த', 'ந', 'ப', 'ம', 'ய', 'ர',
  'ல', 'வ', 'ழ', 'ள', 'ற', 'ன',
];

// Grantha consonants, needed for loanwords — which is most of an Indian restaurant
// menu ("ஜாமூன்", "ஷாமி", "ஸ்").
const GRANTHA = ['ஜ', 'ஷ', 'ஸ', 'ஹ', 'ஶ', 'க்ஷ'];

// Combining vowel signs + pulli. Rendered with a dotted circle so each is visible
// as a standalone button; U+25CC is the convention for showing an isolated mark
// (and is inside the Tamil font subset this app bundles).
const SIGNS = [
  { char: 'ா', label: 'aa' },
  { char: 'ி', label: 'i' },
  { char: 'ீ', label: 'ii' },
  { char: 'ு', label: 'u' },
  { char: 'ூ', label: 'uu' },
  { char: 'ெ', label: 'e' },
  { char: 'ே', label: 'ee' },
  { char: 'ை', label: 'ai' },
  { char: 'ொ', label: 'o' },
  { char: 'ோ', label: 'oo' },
  { char: 'ௌ', label: 'au' },
  { char: '்', label: 'pulli' },
];

const OTHER = ['ஃ', '௦', '௧', '௨', '௩', '௪', '௫', '௬', '௭', '௮', '௯'];

const keyStyle = {
  minWidth: 34,
  padding: '6px 8px',
  borderRadius: 6,
  border: '1px solid #cbd5e1',
  background: '#fff',
  cursor: 'pointer',
  fontSize: '1rem',
  lineHeight: 1.2,
};

const groupLabelStyle = {
  fontSize: '0.7rem',
  fontWeight: 800,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 4,
};

/**
 * @param value      current field value (controlled)
 * @param onChange   receives the next value
 * @param inputRef   ref to the <input> so we can insert at the caret and restore it
 * @param label      accessible description of the field being edited
 */
export default function TamilKeypad({ value, onChange, inputRef, label }) {
  const [open, setOpen] = useState(false);

  const insert = (chars) => {
    const el = inputRef && inputRef.current ? inputRef.current : null;
    const current = String(value || '');

    // Fall back to appending if the input is not mounted for some reason, so a
    // click is never silently lost.
    if (!el) {
      onChange(current + chars);
      return;
    }

    const start = Number.isInteger(el.selectionStart) ? el.selectionStart : current.length;
    const end = Number.isInteger(el.selectionEnd) ? el.selectionEnd : current.length;
    const next = current.slice(0, start) + chars + current.slice(end);
    onChange(next);

    // Restore focus and place the caret after the inserted text. Deferred because
    // React re-renders the controlled input after onChange.
    const caret = start + chars.length;
    window.requestAnimationFrame(() => {
      try {
        el.focus();
        el.setSelectionRange(caret, caret);
      } catch {
        // Non-fatal: the character is already inserted.
      }
    });
  };

  const backspace = () => {
    const el = inputRef && inputRef.current ? inputRef.current : null;
    const current = String(value || '');
    if (!current) return;

    const start = el && Number.isInteger(el.selectionStart) ? el.selectionStart : current.length;
    const end = el && Number.isInteger(el.selectionEnd) ? el.selectionEnd : current.length;

    // Delete a selection if there is one, otherwise the character before the caret.
    const from = start === end ? Math.max(0, start - 1) : start;
    const next = current.slice(0, from) + current.slice(end);
    onChange(next);

    window.requestAnimationFrame(() => {
      try {
        if (el) {
          el.focus();
          el.setSelectionRange(from, from);
        }
      } catch {
        // Non-fatal.
      }
    });
  };

  const renderKeys = (chars) =>
    chars.map((c) => {
      const char = typeof c === 'string' ? c : c.char;
      const hint = typeof c === 'string' ? '' : c.label;
      return (
        <button
          key={char}
          type="button"
          // onMouseDown + preventDefault keeps the input from losing its caret
          // before we read selectionStart.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => insert(char)}
          style={keyStyle}
          title={hint ? `${char} (${hint})` : char}
          aria-label={hint ? `Insert ${char}, ${hint}` : `Insert ${char}`}
        >
          {/* Combining marks render on a dotted circle so the button is legible. */}
          {hint ? `\u25CC${char}` : char}
        </button>
      );
    });

  return (
    <div style={{ marginTop: 4 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={open ? 'Hide Tamil keyboard' : 'Show Tamil keyboard'}
        title={open ? 'Hide Tamil keyboard' : 'Show Tamil keyboard'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 10px',
          borderRadius: 6,
          border: '1px solid #cbd5e1',
          background: open ? '#f5f3ff' : '#fff',
          color: '#4c1d95',
          fontWeight: 700,
          fontSize: '0.8rem',
          cursor: 'pointer',
        }}
      >
        <i className="fas fa-keyboard" aria-hidden="true"></i>
        <span>தமிழ் keyboard</span>
      </button>

      {open ? (
        <div
          role="group"
          aria-label={label ? `Tamil keyboard for ${label}` : 'Tamil keyboard'}
          style={{
            marginTop: 8,
            padding: 10,
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            background: '#f8fafc',
            maxWidth: 560,
          }}
        >
          <div style={{ marginBottom: 8 }}>
            <div style={groupLabelStyle}>Vowels</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{renderKeys(VOWELS)}</div>
          </div>

          <div style={{ marginBottom: 8 }}>
            <div style={groupLabelStyle}>Consonants</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{renderKeys(CONSONANTS)}</div>
          </div>

          <div style={{ marginBottom: 8 }}>
            <div style={groupLabelStyle}>Grantha (loanwords)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{renderKeys(GRANTHA)}</div>
          </div>

          <div style={{ marginBottom: 8 }}>
            <div style={groupLabelStyle}>
              Vowel signs — click a consonant first, then a sign
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{renderKeys(SIGNS)}</div>
          </div>

          <div style={{ marginBottom: 8 }}>
            <div style={groupLabelStyle}>Aytham & digits</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{renderKeys(OTHER)}</div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => insert(' ')}
              style={{ ...keyStyle, minWidth: 90 }}
              aria-label="Insert space"
            >
              Space
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={backspace}
              style={{ ...keyStyle, minWidth: 90 }}
              aria-label="Delete previous character"
            >
              ⌫ Delete
            </button>
          </div>

          <div style={{ marginTop: 8, fontSize: '0.75rem', color: '#64748b' }}>
            Example: <strong>ப</strong> + <strong>{'\u25CC'}ா</strong> = <strong>பா</strong>.
            You can also use your own Tamil keyboard, or paste text in.
          </div>
        </div>
      ) : null}
    </div>
  );
}
// Which language codes this build can offer an on-screen palette for.
//
// The palette is script-specific, so this is deliberately NOT "every non-English
// language in the registry". If a language is added to i18nRegistry.js without a
// palette here, its translation input simply renders without a keypad button —
// the admin can still type or paste — rather than showing a Tamil palette that is
// wrong for that language.
const KEYPAD_LANGUAGES = ['ta'];

export function hasOnScreenKeypad(code) {
  return KEYPAD_LANGUAGES.indexOf(String(code || '').trim().toLowerCase()) !== -1;
}

/**
 * A text input paired with an optional on-screen keypad.
 *
 * WHY THIS WRAPPER EXISTS
 * Both translation editors render their per-language inputs inside a
 * `.map(code => …)`, and the keypad needs a ref to the input it types into.
 * A hook cannot be called inside that map, so each row is its own component
 * instance which owns exactly one `useRef`.
 *
 * `onValueChange` receives the next string (not an event), because the keypad
 * produces a value rather than a DOM event and both callers only ever read
 * `e.target.value` anyway.
 *
 * @param value          current field value (controlled)
 * @param onValueChange  receives the next string value
 * @param showKeypad     render the palette toggle (gate with hasOnScreenKeypad)
 * @param keypadLabel    accessible description of the field being edited
 * @param rest           forwarded to the <input> (id, placeholder, maxLength, …)
 */
export function TamilKeypadInput({
  value,
  onValueChange,
  showKeypad,
  keypadLabel,
  ...rest
}) {
  const inputRef = useRef(null);
  const current = String(value || '');

  return (
    <>
      <input
        {...rest}
        ref={inputRef}
        type="text"
        value={current}
        onChange={(e) => onValueChange(e.target.value)}
      />
      {showKeypad ? (
        <TamilKeypad
          value={current}
          onChange={onValueChange}
          inputRef={inputRef}
          label={keypadLabel}
        />
      ) : null}
    </>
  );
}
